// The trip summary as an image to share (TripSummarySheet's Share): a 4:5 card
// in the brand — ink, paper and one shot of lime — drawn on a canvas. What it
// says comes from `cardContent` (pure, tested); `drawTripCard` only paints it.
// It carries numbers and categories, never item or member names: a shared
// image leaves the household.
//
// Colours and the font are read from the brand's CSS variables at draw time
// (brand.css, tokens.css), so no colour lives here.

import { MARK_PATH, MARK_STROKE } from './brandMark'
import { clock, versusUsual, type TripRecap } from './trips'

export const CARD_W = 1080
export const CARD_H = 1350
/** Splits that fit on the card; the rest are counted underneath. */
export const CARD_SPLITS = 5
/** The card's call to action; the shared link (signupUrl) is what it opens. */
export const CARD_CTA = 'Try Matlist'

/** Create account, opened straight on the landing page (LoginPage reads `?signup`). */
export const signupUrl = (origin: string = window.location.origin) => `${origin}/?signup`

export interface CardSplit { name: string; tint: string; items: number; time: string | null; fraction: number }
export interface CardContent {
  date: string
  hero: string
  heroLabel: string
  stats: { value: string; label: string }[]
  versus: string | null
  splits: CardSplit[]
  more: number
}

/** What the card (and the sheet) shows: the hero is the time, or the item count when untimed. */
export function cardContent(
  r: TripRecap,
  finishedAt: Date,
  nameOf: (categoryId: string | null) => string,
  tintOf: (categoryId: string | null) => string,
  locale?: string,
): CardContent {
  const stats: { value: string; label: string }[] = []
  if (r.ms !== null) stats.push({ value: String(r.items), label: 'Items' })
  if (r.perItem !== null) stats.push({ value: clock(r.perItem), label: 'Per item' })
  stats.push({ value: String(r.splits.length), label: r.splits.length === 1 ? 'Section' : 'Sections' })
  const longest = Math.max(1, ...r.splits.map(s => s.ms))
  const shown = r.splits.slice(0, CARD_SPLITS)
  return {
    date: finishedAt.toLocaleDateString(locale, { weekday: 'short', day: 'numeric', month: 'short' }),
    hero: r.ms !== null ? clock(r.ms) : String(r.items),
    heroLabel: r.ms !== null ? 'Time in store' : (r.items === 1 ? 'Item' : 'Items'),
    stats,
    versus: versusUsual(r),
    splits: shown.map(s => ({
      name: nameOf(s.categoryId),
      tint: tintOf(s.categoryId),
      items: s.items,
      time: r.ms !== null ? clock(s.ms) : null,
      fraction: r.ms !== null ? s.ms / longest : s.items / Math.max(...r.splits.map(x => x.items)),
    })),
    more: r.splits.length - shown.length,
  }
}

export interface CardPalette { ink: string; paper: string; lime: string; font: string }

/** The brand's colours and font as the page has them now, and a tint var resolved to a colour. */
export function readPalette(root: HTMLElement = document.documentElement): CardPalette & { resolve: (v: string) => string } {
  const css = getComputedStyle(root)
  const get = (name: string) => css.getPropertyValue(name).trim()
  return {
    ink: get('--matlist-ink'),
    paper: get('--matlist-paper'),
    lime: get('--matlist-lime'),
    font: get('--matlist-font') || 'system-ui, sans-serif',
    resolve: v => {
      const m = /^var\((--[\w-]+)\)$/.exec(v)
      return (m ? get(m[1]) : v) || get('--matlist-muted')
    },
  }
}

type Ctx = CanvasRenderingContext2D

function text(ctx: Ctx, s: string, x: number, y: number, font: string, color: string, opts: { align?: CanvasTextAlign; tracking?: string; stretch?: CanvasFontStretch; alpha?: number } = {}) {
  ctx.save()
  ctx.font = font
  ctx.fillStyle = color
  ctx.globalAlpha = opts.alpha ?? 1
  ctx.textAlign = opts.align ?? 'left'
  if ('letterSpacing' in ctx) ctx.letterSpacing = opts.tracking ?? '0px'
  if ('fontStretch' in ctx) ctx.fontStretch = opts.stretch ?? 'normal'
  ctx.fillText(s, x, y)
  ctx.restore()
}

function rect(ctx: Ctx, x: number, y: number, w: number, h: number, color: string, alpha = 1) {
  ctx.save()
  ctx.globalAlpha = alpha
  ctx.fillStyle = color
  ctx.beginPath()
  ctx.roundRect(x, y, w, h, h / 2)
  ctx.fill()
  ctx.restore()
}

/** Paints the card at CARD_W × CARD_H. */
export function drawTripCard(ctx: Ctx, c: CardContent, p: CardPalette) {
  const pad = 88
  const right = CARD_W - pad
  const f = (weight: number, size: number) => `${weight} ${size}px ${p.font}`
  const label = (s: string, x: number, y: number, align: CanvasTextAlign = 'left') =>
    text(ctx, s.toUpperCase(), x, y, f(700, 26), p.paper, { align, tracking: '4px', alpha: 0.6 })

  ctx.fillStyle = p.ink
  ctx.fillRect(0, 0, CARD_W, CARD_H)

  label('Trip finished', pad, 130)
  label(c.date, right, 130, 'right')

  text(ctx, c.hero, pad - 8, 360, f(800, 240), p.paper, { stretch: 'condensed', tracking: '-5px' })
  label(c.heroLabel, pad, 420)

  // The stats, evenly across the width.
  const col = (right - pad) / Math.max(c.stats.length, 1)
  c.stats.forEach((s, i) => {
    text(ctx, s.value, pad + i * col, 540, f(800, 80), p.paper, { stretch: 'condensed' })
    label(s.label, pad + i * col, 584)
  })

  let y = 670
  if (c.versus) {
    text(ctx, c.versus, pad, y, f(600, 34), p.lime)
    y += 76
  }

  // Splits: name and numbers over a lime bar, like a workout's laps.
  label('Splits', pad, y)
  y += 66
  const rowH = 72
  for (const s of c.splits) {
    ctx.save()
    ctx.fillStyle = s.tint
    ctx.beginPath()
    ctx.arc(pad + 9, y - 11, 9, 0, Math.PI * 2)
    ctx.fill()
    ctx.restore()
    text(ctx, s.name, pad + 34, y, f(600, 32), p.paper)
    const right2 = s.time ? right - 130 : right
    text(ctx, s.items === 1 ? '1 item' : `${s.items} items`, right2, y, f(500, 28), p.paper, { align: 'right', alpha: 0.6 })
    if (s.time) text(ctx, s.time, right, y, f(700, 32), p.paper, { align: 'right' })
    rect(ctx, pad, y + 18, right - pad, 10, p.paper, 0.12)
    rect(ctx, pad, y + 18, Math.max(10, (right - pad) * s.fraction), 10, p.lime)
    y += rowH
  }
  if (c.more > 0) label(`${c.more} more ${c.more === 1 ? 'section' : 'sections'}`, pad, y)

  // The footer: the mark, and a button-shaped invitation with the address
  // under it. A picture can't be tapped — the link that opens Create account
  // travels beside the image (shareTripCard).
  const footY = CARD_H - 120
  const markSize = 64
  ctx.save()
  ctx.translate(pad - 6, footY - markSize * 0.62)
  ctx.scale(markSize / 100, markSize / 100)
  ctx.strokeStyle = p.lime
  ctx.lineWidth = MARK_STROKE
  ctx.lineCap = 'round'
  ctx.lineJoin = 'round'
  ctx.stroke(new Path2D(MARK_PATH))
  ctx.restore()
  text(ctx, 'matlist', pad + 70, footY, `italic ${f(800, 44)}`, p.paper, { stretch: 'expanded' })

  const cta = CARD_CTA.toUpperCase()
  ctx.save()
  ctx.font = f(700, 28)
  if ('letterSpacing' in ctx) ctx.letterSpacing = '4px'
  const pillW = ctx.measureText(cta).width + 72
  ctx.restore()
  const pillH = 76
  const pillTop = footY - 52
  ctx.save()
  ctx.fillStyle = p.lime
  ctx.beginPath()
  ctx.roundRect(right - pillW, pillTop, pillW, pillH, 16)
  ctx.fill()
  ctx.restore()
  text(ctx, cta, right - pillW / 2, pillTop + 48, f(700, 28), p.ink, { align: 'center', tracking: '4px' })
  text(ctx, 'matlist.app', right - pillW / 2, pillTop + pillH + 44, f(600, 26), p.paper, { align: 'center', alpha: 0.6 })
}

export type ShareOutcome = 'shared' | 'downloaded' | 'cancelled' | 'failed'

/**
 * Renders the card to a PNG and hands it to the share sheet (iPhone, Android),
 * or downloads it where files can't be shared (most desktops).
 */
export async function shareTripCard(c: CardContent, p: CardPalette): Promise<ShareOutcome> {
  try {
    await document.fonts?.load(`800 100px ${p.font}`).catch(() => undefined)
    const canvas = document.createElement('canvas')
    canvas.width = CARD_W
    canvas.height = CARD_H
    const ctx = canvas.getContext('2d')
    if (!ctx) return 'failed'
    drawTripCard(ctx, c, p)
    const blob = await new Promise<Blob | null>(resolve => canvas.toBlob(resolve, 'image/png'))
    if (!blob) return 'failed'
    const file = new File([blob], 'matlist-trip.png', { type: 'image/png' })
    if (navigator.canShare?.({ files: [file] })) {
      try {
        // The link rides as text: share targets that take a file and a `url` together often drop the url.
        await navigator.share({ files: [file], title: 'Trip finished', text: `${CARD_CTA}: ${signupUrl()}` })
        return 'shared'
      } catch (e) {
        return (e as Error).name === 'AbortError' ? 'cancelled' : 'failed'
      }
    }
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = file.name
    a.click()
    setTimeout(() => URL.revokeObjectURL(url), 1000)
    return 'downloaded'
  } catch {
    return 'failed'
  }
}
